import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Punishments.css';

const STATUS_FILTER_OPTIONS = [
  { id: '', name: 'Все статусы' },
  { id: 'pending', name: 'На рассмотрении' },
  { id: 'approved', name: 'Одобрено' },
  { id: 'rejected', name: 'Отклонено' },
];

const statusLabels = {
  pending: 'На рассмотрении',
  approved: 'Одобрено',
  rejected: 'Отклонено',
};

const statusClass = {
  pending: 'punishment-status-pending',
  approved: 'punishment-status-approved',
  rejected: 'punishment-status-rejected',
};

const METRIC_SHORT = {
  punishments_discord: 'Discord',
  punishments_as: 'AS',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${h}:${m}`;
};

const Punishments = () => {
  const [selectedRuleId, setSelectedRuleId] = useState(''); // '' = все правила
  const [filterRules, setFilterRules] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [hideCleaningRole, setHideCleaningRole] = useState(false);
  const [hideJuniorRole, setHideJuniorRole] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimerRef = useRef(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [warningModal, setWarningModal] = useState({
    open: false,
    discordMessageId: null,
    action: null,
    warningText: '',
  });
  const [proofLightbox, setProofLightbox] = useState(null); // { urls: string[], index: number } | null
  const [lightboxScale, setLightboxScale] = useState(1);
  const [lightboxPan, setLightboxPan] = useState({ x: 0, y: 0 });
  const lightboxTouchRef = React.useRef(null);
  const lightboxPanRef = React.useRef(null);
  const lightboxScaleRef = React.useRef(1);
  lightboxScaleRef.current = lightboxScale;

  const PAGE_SIZE = 20;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchPunishments = useCallback(async (page, search) => {
    setLoading(true);
    setError('');
    const offset = (page - 1) * PAGE_SIZE;
    try {
      const params = new URLSearchParams();
      if (selectedRuleId) params.set('ingest_rule_id', String(selectedRuleId));
      if (statusFilter) params.set('status', statusFilter);
      if (hideCleaningRole) params.set('hide_cleaning', '1');
      if (hideJuniorRole) params.set('hide_junior', '1');
      if (search) params.set('search', search);
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offset));
      const res = await fetch(`/api/punishments?${params}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Ошибка загрузки наказаний');
      }
      const data = await res.json();
      setItems(data.items || []);
      setTotal(data.total ?? 0);
      if (Array.isArray(data.filterRules)) setFilterRules(data.filterRules);
      setCurrentPage(page);
    } catch (err) {
      setError(err.message);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [selectedRuleId, statusFilter, hideCleaningRole, hideJuniorRole]);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
    fetchPunishments(1, debouncedSearch);
  }, [selectedRuleId, statusFilter, hideCleaningRole, hideJuniorRole, debouncedSearch, fetchPunishments]);

  useEffect(() => {
    if (
      selectedRuleId &&
      filterRules.length > 0 &&
      !filterRules.some((r) => String(r.id) === String(selectedRuleId))
    ) {
      setSelectedRuleId('');
    }
  }, [filterRules, selectedRuleId]);

  useEffect(() => {
    if (total > 0 && currentPage > totalPages && totalPages >= 1) {
      fetchPunishments(totalPages, debouncedSearch);
    }
  }, [total, currentPage, totalPages]);

  const goToPage = (page) => {
    const p = Math.max(1, Math.min(totalPages, page));
    if (p !== currentPage) fetchPunishments(p, debouncedSearch);
  };

  const [pageInput, setPageInput] = useState('');
  const commitPageInput = () => {
    const n = parseInt(pageInput, 10);
    if (!isNaN(n) && n >= 1 && n <= totalPages) goToPage(n);
    setPageInput('');
  };

  const getToken = () => localStorage.getItem('token');

  const runAction = async (discordMessageId, action, body = null) => {
    setActionLoading(discordMessageId + action);
    try {
      const url = `/api/punishments/${discordMessageId}/${action}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Ошибка');
      setWarningModal({ open: false, discordMessageId: null, action: null, warningText: '' });
      await fetchPunishments(currentPage, debouncedSearch);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = (p) => runAction(p.discord_message_id, 'approve');
  const handleReject = (p) => runAction(p.discord_message_id, 'reject');
  const handleChangeDecision = (p) => runAction(p.discord_message_id, 'change-decision');
  const openWarningModal = (p, action) => setWarningModal({ open: true, discordMessageId: p.discord_message_id, action, warningText: '' });
  const submitWarning = () => {
    if (!warningModal.warningText.trim()) return;
    const action = warningModal.action === 'approve_with_warning' ? 'approve-with-warning' : 'reject-with-warning';
    runAction(warningModal.discordMessageId, action, { warning_text: warningModal.warningText.trim() });
  };

  const lightboxUrls = proofLightbox?.urls ?? [];
  const lightboxIndex = proofLightbox?.index ?? 0;
  const lightboxCurrentUrl = lightboxUrls[lightboxIndex] || null;
  const lightboxHasMultiple = lightboxUrls.length > 1;
  const goLightboxPrev = () => {
    if (!proofLightbox || lightboxUrls.length <= 1) return;
    setProofLightbox((prev) => ({ ...prev, index: (prev.index - 1 + prev.urls.length) % prev.urls.length }));
    setLightboxScale(1);
    setLightboxPan({ x: 0, y: 0 });
  };
  const goLightboxNext = () => {
    if (!proofLightbox || lightboxUrls.length <= 1) return;
    setProofLightbox((prev) => ({ ...prev, index: (prev.index + 1) % prev.urls.length }));
    setLightboxScale(1);
    setLightboxPan({ x: 0, y: 0 });
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!proofLightbox) return;
      if (e.key === 'Escape') setProofLightbox(null);
      if (lightboxHasMultiple && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        if (e.key === 'ArrowLeft') goLightboxPrev();
        else goLightboxNext();
      }
    };
    if (proofLightbox) window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [proofLightbox, lightboxHasMultiple]);

  useEffect(() => {
    if (proofLightbox) {
      setLightboxScale(1);
      setLightboxPan({ x: 0, y: 0 });
    }
  }, [proofLightbox]);

  const getTouchDistance = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
  const maxPan = () => Math.max(window.innerWidth, window.innerHeight) * 1.2;

  const onLightboxTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      lightboxPanRef.current = null;
      lightboxTouchRef.current = {
        initialDistance: getTouchDistance(e.touches[0], e.touches[1]),
        initialScale: lightboxScaleRef.current,
      };
    } else if (e.touches.length === 1) {
      lightboxTouchRef.current = null;
      lightboxPanRef.current = {
        initialTouch: { x: e.touches[0].clientX, y: e.touches[0].clientY },
        initialPan: { ...lightboxPan },
      };
    }
  };
  const onLightboxTouchMove = (e) => {
    if (e.touches.length === 2 && lightboxTouchRef.current) {
      e.preventDefault();
      const { initialDistance, initialScale } = lightboxTouchRef.current;
      const currentDistance = getTouchDistance(e.touches[0], e.touches[1]);
      const scale = Math.min(4, Math.max(0.5, initialScale * (currentDistance / initialDistance)));
      setLightboxScale(scale);
    } else if (e.touches.length === 1 && lightboxPanRef.current) {
      e.preventDefault();
      const { initialTouch, initialPan } = lightboxPanRef.current;
      const dx = e.touches[0].clientX - initialTouch.x;
      const dy = e.touches[0].clientY - initialTouch.y;
      const limit = maxPan();
      const x = Math.max(-limit, Math.min(limit, initialPan.x + dx));
      const y = Math.max(-limit, Math.min(limit, initialPan.y + dy));
      setLightboxPan({ x, y });
    }
  };
  const onLightboxTouchEnd = (e) => {
    if (e.touches.length < 2) lightboxTouchRef.current = null;
    if (e.touches.length === 0) lightboxPanRef.current = null;
  };

  useEffect(() => {
    if (!filtersOpen) return undefined;
    const closeFilters = (e) => {
      if (!e.target.closest('.punishments-filters-wrap')) setFiltersOpen(false);
    };
    document.addEventListener('click', closeFilters);
    return () => document.removeEventListener('click', closeFilters);
  }, [filtersOpen]);

  const moderatorDisplay = (p) => p.moderator_nickname || (p.moderator_discord_id ? `@${p.moderator_discord_id}` : '—');

  const duplicateTargetIds = React.useMemo(() => {
    const count = new Map();
    items.forEach((p) => {
      const id = p.target_id != null ? String(p.target_id).trim() : '';
      if (id) count.set(id, (count.get(id) || 0) + 1);
    });
    const dupes = new Set();
    count.forEach((c, id) => { if (c > 1) dupes.add(id); });
    return dupes;
  }, [items]);

  const isDuplicate = (p) => {
    const id = p.target_id != null ? String(p.target_id).trim() : '';
    return id && duplicateTargetIds.has(id);
  };

  return (
    <div className="punishments-container">
      <div className="punishments-content">
        {error && (
          <div className="punishments-alert punishments-alert-error">
            {error}
          </div>
        )}

        <div className="punishments-cards-container">
          <div className="punishments-toolbar">
            <input
              type="text"
              className="punishments-search"
              placeholder="Поиск по причине, нарушителю, команде, модератору..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="punishments-filters-wrap">
              <button
                type="button"
                className={`punishments-filters-btn${filtersOpen ? ' active' : ''}${
                  selectedRuleId || statusFilter !== 'pending' || hideCleaningRole || hideJuniorRole
                    ? ' has-filters'
                    : ''
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setFiltersOpen((v) => !v);
                }}
                aria-expanded={filtersOpen}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                <span>Фильтры</span>
                {(selectedRuleId || statusFilter !== 'pending' || hideCleaningRole || hideJuniorRole) && (
                  <span className="punishments-filters-dot" aria-hidden />
                )}
                <svg
                  className={`punishments-filters-chevron${filtersOpen ? ' open' : ''}`}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  aria-hidden
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {filtersOpen && (
                <div className="punishments-filters-dropdown" onClick={(e) => e.stopPropagation()}>
                  <div className="punishments-filters-dropdown-title">Правило учёта</div>
                  <button
                    type="button"
                    className={`punishments-filters-option ${selectedRuleId === '' ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedRuleId('');
                      setFiltersOpen(false);
                    }}
                  >
                    Все правила
                  </button>
                  {filterRules.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      className={`punishments-filters-option ${String(selectedRuleId) === String(r.id) ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedRuleId(String(r.id));
                        setFiltersOpen(false);
                      }}
                    >
                      <span className="punishments-filters-option-main">{r.display_name}</span>
                      {(r.enabled === false || METRIC_SHORT[r.target_metric]) && (
                        <span className="punishments-filters-option-meta">
                          {r.enabled === false ? 'выкл.' : ''}
                          {r.enabled === false && METRIC_SHORT[r.target_metric] ? ' · ' : ''}
                          {METRIC_SHORT[r.target_metric] || ''}
                        </span>
                      )}
                    </button>
                  ))}

                  <div className="punishments-filters-dropdown-divider" />
                  <div className="punishments-filters-dropdown-title">Статус</div>
                  {STATUS_FILTER_OPTIONS.map((opt) => (
                    <button
                      key={opt.id || 'all'}
                      type="button"
                      className={`punishments-filters-option ${statusFilter === opt.id ? 'active' : ''}`}
                      onClick={() => {
                        setStatusFilter(opt.id);
                        setFiltersOpen(false);
                      }}
                    >
                      {opt.name}
                    </button>
                  ))}

                  <div className="punishments-filters-dropdown-divider" />
                  <div className="punishments-filters-dropdown-title">Прочее</div>
                  <label className="punishments-filters-checkbox">
                    <input
                      type="checkbox"
                      checked={hideCleaningRole}
                      onChange={(e) => setHideCleaningRole(e.target.checked)}
                    />
                    <span>Скрыть Cleaning</span>
                  </label>
                  <label className="punishments-filters-checkbox">
                    <input
                      type="checkbox"
                      checked={hideJuniorRole}
                      onChange={(e) => setHideJuniorRole(e.target.checked)}
                    />
                    <span>Скрыть Junior</span>
                  </label>
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="punishments-loading">Загрузка...</div>
          ) : items.length === 0 ? (
            <div className="punishments-empty">
              <p>
                {searchQuery.trim()
                  ? 'Ничего не найдено по запросу.'
                  : `Нет наказаний${statusFilter ? ` со статусом «${statusLabels[statusFilter] || statusFilter}»` : ''}.`}
              </p>
            </div>
          ) : (
            <>
            <div className="punishments-grid">
              {items.map((p) => (
              <div key={p.id} className="punishment-card">
                {isDuplicate(p) && (
                  <div className="punishment-card-duplicate-warning" role="alert">
                    Обнаружен дубликат
                  </div>
                )}
                <div className="punishment-card-header">
                  <div className="punishment-card-header-main">
                    <h3 className="punishment-card-title">
                      {p.command || '—'}
                    </h3>
                    {p.source_link && (
                      <a
                        href={p.source_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="punishment-btn-link-message"
                      >
                        Сообщение
                      </a>
                    )}
                  </div>
                  <div className="punishment-card-header-badges">
                    {(p.rule_display_name || p.server) && (
                      <span className="punishment-rule-badge" title="Правило учёта">
                        {p.rule_display_name || p.server}
                      </span>
                    )}
                    <span className={`punishment-status-badge ${statusClass[p.status] || ''}`}>
                      {statusLabels[p.status] || p.status}
                    </span>
                    {p.status !== 'pending' && (p.decided_by_nickname || p.decided_by_discord_id || p.updated_at) && (
                      <span className="punishment-status-info-wrap">
                        <span className="punishment-status-info-btn" aria-label="Кто рассмотрел и когда">i</span>
                        <span className="punishment-status-info-tooltip">
                          {p.decided_by_moderator_nickname || p.decided_by_nickname || (p.decided_by_discord_id ? `Discord ID: ${p.decided_by_discord_id}` : '—')}
                          {p.updated_at && (
                            <>
                              <br />
                              {formatDate(p.updated_at)}
                            </>
                          )}
                        </span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="punishment-card-section">
                  {p.target_id && (
                    <div className="punishment-card-row">
                      <div className="punishment-card-row-item">
                        <span className="punishment-label">Нарушитель:</span>{' '}
                        <span className="punishment-target-id">{p.target_id}</span>
                      </div>
                    </div>
                  )}
                  <p className="punishment-card-reason">
                    <span className="punishment-label">Причина:</span> {p.reason || '—'}
                  </p>
                  <div className="punishment-card-row">
                    <div className="punishment-card-row-item">
                      <span className="punishment-label">Время выдачи:</span>{' '}
                      <span className="punishment-date">{formatDate(p.created_at)}</span>
                    </div>
                  </div>
                </div>
                {(() => {
                  const proofs = Array.isArray(p.proof) ? p.proof : (p.proof ? [p.proof] : []);
                  const firstUrl = proofs[0];
                  const extraCount = proofs.length - 1;
                  if (!firstUrl && !proofs.length) return null;
                  return (
                    <div className="punishment-card-proof">
                      <span className="punishment-label">Доказательства:</span>
                      {firstUrl && firstUrl.startsWith('http') ? (
                        <div className="punishment-proof-wrap">
                          <div
                            className="punishment-proof-thumb-wrap"
                            onClick={() => {
                              setProofLightbox(proofs.length ? { urls: proofs, index: 0 } : null);
                            }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && proofs.length && setProofLightbox({ urls: proofs, index: 0 })}
                          >
                            <img
                              src={firstUrl}
                              alt="Доказательства"
                              className="punishment-proof-image punishment-proof-image-clickable"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const link = e.target.nextElementSibling;
                                if (link) link.style.display = 'inline';
                              }}
                            />
                            {extraCount > 0 && (
                              <span className="punishment-proof-extra-badge">+{extraCount}</span>
                            )}
                            <a href={firstUrl} target="_blank" rel="noopener noreferrer" className="punishment-link punishment-proof-fallback" style={{ display: 'none' }}>
                              {firstUrl}
                            </a>
                          </div>
                        </div>
                      ) : (
                        <span>{firstUrl || 'Не приложено'}</span>
                      )}
                    </div>
                  );
                })()}
                <div className="punishment-card-meta punishment-card-meta-middle">
                  <span className="punishment-meta-item">
                    <span className="punishment-label">Модератор:</span>{' '}
                    <span className="punishment-moderator-name">{moderatorDisplay(p)}</span>
                  </span>
                  <span className="punishment-meta-item">
                    <span className="punishment-label">Роли:</span>{' '}
                    {(() => {
                      const rolesLabels = [];
                      if (p.moderator_has_cleaning_role) rolesLabels.push('Cleaning');
                      if (p.moderator_has_junior_role) rolesLabels.push('Junior');
                      if (!rolesLabels.length) return '—';
                      return rolesLabels.map((label) => (
                        <span key={label} className="punishment-badge-cleaning-inline">
                          {label}
                        </span>
                      ));
                    })()}
                  </span>
                </div>

                {p.status === 'pending' && (
                  <div className="punishment-card-actions punishment-buttons-rows">
                    <div className="punishment-buttons-row punishment-buttons-row-2">
                      <button
                        type="button"
                        className="punishment-btn punishment-btn-approve"
                        onClick={() => handleApprove(p)}
                        disabled={actionLoading !== null}
                      >
                        {actionLoading === p.discord_message_id + 'approve' ? '...' : 'Одобрить'}
                      </button>
                      <button
                        type="button"
                        className="punishment-btn punishment-btn-reject"
                        onClick={() => handleReject(p)}
                        disabled={actionLoading !== null}
                      >
                        {actionLoading === p.discord_message_id + 'reject' ? '...' : 'Отклонить'}
                      </button>
                    </div>
                    <div className="punishment-buttons-row punishment-buttons-row-1">
                      <button
                        type="button"
                        className="punishment-btn punishment-btn-approve-warn"
                        onClick={() => openWarningModal(p, 'approve_with_warning')}
                        disabled={actionLoading !== null}
                      >
                        Одобрить + предупреждение
                      </button>
                    </div>
                    <div className="punishment-buttons-row punishment-buttons-row-1">
                      <button
                        type="button"
                        className="punishment-btn punishment-btn-reject-warn"
                        onClick={() => openWarningModal(p, 'reject_with_warning')}
                        disabled={actionLoading !== null}
                      >
                        Отклонить + предупреждение
                      </button>
                    </div>
                  </div>
                )}
                {(p.status === 'approved' || p.status === 'rejected') && (
                  <div className="punishment-card-actions punishment-buttons-row punishment-buttons-row-1">
                    <button
                      type="button"
                      className="punishment-btn punishment-btn-change"
                      onClick={() => handleChangeDecision(p)}
                      disabled={actionLoading !== null}
                    >
                      {actionLoading === p.discord_message_id + 'change-decision' ? '...' : 'Изменить решение'}
                    </button>
                  </div>
                )}
              </div>
            ))}
            </div>
            <div className="punishments-pagination">
              <div className="punishments-pagination-summary">
                {total > 0 && `${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, total)} из ${total}`}
              </div>
              {totalPages > 1 && (
                <div className="punishments-pagination-controls">
                  <button
                    type="button"
                    className="punishments-pagination-btn"
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage <= 1}
                  >
                    &laquo;
                  </button>
                  {(() => {
                    const pages = [];
                    const range = 2;
                    let start = Math.max(1, currentPage - range);
                    let end = Math.min(totalPages, currentPage + range);
                    if (start > 1) { pages.push(1); if (start > 2) pages.push('...'); }
                    for (let i = start; i <= end; i++) pages.push(i);
                    if (end < totalPages) { if (end < totalPages - 1) pages.push('...'); pages.push(totalPages); }
                    return pages.map((p, idx) =>
                      p === '...' ? (
                        <span key={`dots-${idx}`} className="punishments-pagination-dots">...</span>
                      ) : (
                        <button
                          key={p}
                          type="button"
                          className={`punishments-pagination-btn punishments-pagination-num ${p === currentPage ? 'active' : ''}`}
                          onClick={() => goToPage(p)}
                        >
                          {p}
                        </button>
                      )
                    );
                  })()}
                  <button
                    type="button"
                    className="punishments-pagination-btn"
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                  >
                    &raquo;
                  </button>
                  {totalPages > 7 && (
                    <input
                      type="number"
                      className="punishments-pagination-input"
                      min={1}
                      max={totalPages}
                      placeholder="#"
                      value={pageInput}
                      onChange={(e) => setPageInput(e.target.value)}
                      onBlur={commitPageInput}
                      onKeyDown={(e) => e.key === 'Enter' && commitPageInput()}
                      aria-label="Перейти на страницу"
                    />
                  )}
                </div>
              )}
            </div>
            </>
          )}
        </div>
      </div>

      {proofLightbox && lightboxCurrentUrl && (
        <div
          className="punishment-proof-lightbox"
          onClick={() => setProofLightbox(null)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Escape' && setProofLightbox(null)}
          aria-label="Закрыть просмотр"
        >
          <div className="punishment-proof-lightbox-viewport">
            <div
              className="punishment-proof-lightbox-image-wrap"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={onLightboxTouchStart}
              onTouchMove={onLightboxTouchMove}
              onTouchEnd={onLightboxTouchEnd}
              onTouchCancel={onLightboxTouchEnd}
              style={{
                transform: `translate(calc(-50% + ${lightboxPan.x}px), calc(-50% + ${lightboxPan.y}px)) scale(${lightboxScale})`,
              }}
            >
              <img
                src={lightboxCurrentUrl}
                alt={`Доказательства ${lightboxHasMultiple ? lightboxIndex + 1 + ' / ' + lightboxUrls.length : ''}`}
                className="punishment-proof-lightbox-image"
                draggable={false}
              />
            </div>
          </div>
          {lightboxHasMultiple && (
            <>
              <button type="button" className="punishment-proof-lightbox-prev" onClick={(e) => { e.stopPropagation(); goLightboxPrev(); }} aria-label="Предыдущий скрин">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6" /></svg>
              </button>
              <button type="button" className="punishment-proof-lightbox-next" onClick={(e) => { e.stopPropagation(); goLightboxNext(); }} aria-label="Следующий скрин">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
              </button>
              <div className="punishment-proof-lightbox-dots" onClick={(e) => e.stopPropagation()}>
                {lightboxUrls.map((_, i) => (
                  <button key={i} type="button" className={`punishment-proof-lightbox-dot ${i === lightboxIndex ? 'active' : ''}`} onClick={() => { setProofLightbox((prev) => ({ ...prev, index: i })); setLightboxScale(1); setLightboxPan({ x: 0, y: 0 }); }} aria-label={`Скрин ${i + 1}`} />
                ))}
              </div>
            </>
          )}
          <button type="button" className="punishment-proof-lightbox-close" onClick={(e) => { e.stopPropagation(); setProofLightbox(null); }} aria-label="Закрыть">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {warningModal.open && (
        <div className="punishment-warning-modal-overlay" onClick={() => setWarningModal((m) => ({ ...m, open: false }))}>
          <div className="punishment-warning-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="punishment-warning-modal-title">
              {warningModal.action === 'approve_with_warning' ? 'Одобрить + предупреждение' : 'Отклонить + предупреждение'}
            </h3>
            <label className="punishment-warning-modal-label">Текст предупреждения для модератора</label>
            <textarea
              className="punishment-warning-modal-textarea"
              value={warningModal.warningText}
              onChange={(e) => setWarningModal((m) => ({ ...m, warningText: e.target.value }))}
              placeholder="Введите текст предупреждения..."
              rows={4}
              maxLength={1000}
            />
            <div className="punishment-warning-modal-footer">
              <button type="button" className="punishment-btn punishment-btn-change" onClick={() => setWarningModal((m) => ({ ...m, open: false }))}>
                Отмена
              </button>
              <button
                type="button"
                className="punishment-btn punishment-btn-approve-warn"
                onClick={submitWarning}
                disabled={!warningModal.warningText.trim() || actionLoading !== null}
              >
                Отправить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Punishments;
